import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { SEO } from "../components/SEO";
import { TimelineSection } from "../components/Timeline";
import { RecognitionSection } from "../components/Recognition";
import { completedProjects } from "../data/site";

const totalArea = completedProjects.reduce((sum, p) => sum + parseInt(p.area.replace(/,/g, ""), 10), 0);

export default function TrackRecord() {
  return (
    <main data-testid="track-record-page">
      <SEO
        title="Track Record — Parsn Group"
        description="81 projects across five decades — from St. Xavier's College and K.E.M. Hospital to High Court Judges Residency, BKC and redevelopment across Mumbai."
        path="/track-record"
      />
      <section className="bg-white pt-32 sm:pt-44 pb-16 sm:pb-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="overline-label mb-4">Track record</p>
            <h1 className="font-serif text-4xl sm:text-6xl text-ink tracking-tight leading-[1.05] max-w-3xl">
              Proof, not <span className="italic text-navy">promises.</span>
            </h1>
            <p className="mt-6 max-w-xl text-base sm:text-lg text-gray-600 leading-relaxed">
              From piling works for Bombay Port Trust in the early 1970s to redevelopment today —
              residential, institutional and civic projects across Mumbai, Pune and Chennai.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="mt-14 grid grid-cols-2 lg:grid-cols-3 border-t border-l border-gray-200">
            {[
              { value: "11+", label: "Residential projects completed" },
              { value: `${Math.round(totalArea / 1000)}k+`, label: "Sq ft constructed, Mumbai" },
              { value: "1972", label: "First handover" },
            ].map((s) => (
              <div key={s.label} className="border-b border-r border-gray-200 p-6 sm:p-10">
                <p className="font-serif text-4xl sm:text-5xl text-navy tracking-tight">{s.value}</p>
                <p className="mt-3 text-xs sm:text-sm uppercase tracking-[0.15em] text-gray-500">{s.label}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section className="bg-white pb-24 sm:pb-32">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="mb-10">
            <h2 className="font-serif text-2xl sm:text-4xl text-ink tracking-tight">Completed residential projects</h2>
          </Reveal>
          <Reveal className="border border-gray-200 overflow-x-auto">
            <table data-testid="completed-projects-table" className="w-full min-w-[560px] text-left">
              <thead>
                <tr className="bg-navy text-white">
                  <th className="text-[11px] font-semibold uppercase tracking-[0.2em] px-6 py-4">Project</th>
                  <th className="text-[11px] font-semibold uppercase tracking-[0.2em] px-6 py-4">Construction area (sq ft)</th>
                  <th className="text-[11px] font-semibold uppercase tracking-[0.2em] px-6 py-4">Year completed</th>
                </tr>
              </thead>
              <tbody>
                {completedProjects.map((p, i) => (
                  <tr key={p.name} className={`transition-colors duration-300 hover:bg-paper ${i % 2 ? "bg-white" : "bg-white" } border-t border-gray-200`}>
                    <td className="px-6 py-4 font-serif text-lg text-ink tracking-tight">{p.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{p.area}</td>
                    <td className="px-6 py-4 text-sm font-semibold text-navy">{p.year}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Reveal>
        </div>
      </section>

      <TimelineSection />

      <RecognitionSection />

      <section className="bg-white border-t border-gray-200 py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-center justify-between gap-8">
            <h2 className="font-serif text-2xl sm:text-4xl text-ink tracking-tight max-w-xl leading-tight">
              See what the next chapter looks like
            </h2>
            <Link
              to="/projects"
              data-testid="track-record-cta-projects"
              className="inline-flex items-center gap-2 bg-brand text-white text-sm font-semibold px-7 py-4 transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-[#e0671a]"
            >
              View ongoing projects <ArrowRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
